import { Icon, Notice, SliderModal, Text, HStack, VStack, Select } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { ReportingEnumsProfitabilityReportMarkingTypeEnum } from '@genial/services/dist/models';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Button, Pressable } from 'native-base';
import { useMemo, useState } from 'react';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../routes/types';
import { useCustomerDetailsActions } from '../../../../../../store/customerDetail/actions';

import {
  buildYearOptions,
  initialProfitabilityType,
  monthsOptions,
  profitabilityTypeOptions,
} from './constants';

export const ProfitabilityReportDownload = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const direction = useBreakpointValue({ base: 'bottom', lg: 'right' });
  const sliderModalWidth = useBreakpointValue({ base: 1, lg: 0.3 });
  const { params } =
    useRoute<RouteProp<SignedInRootStackParamList, 'ProfitabilityReportDownload'>>();

  const yearsOptions = useMemo(() => {
    const endYear = new Date().getFullYear();
    const startYear = 1950;
    return buildYearOptions(startYear, endYear);
  }, []);

  const directionIsBottom = direction === 'bottom';

  const [initialMonth, setInitialMonth] = useState('');
  const [initialYear, setInitialYear] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profitabilityType, setProfitabilityType] = useState(initialProfitabilityType);

  const { downloadProfitabilityReport } = useCustomerDetailsActions();
  const isInvalidDownload = !profitabilityType || !initialMonth || !initialYear || isLoading;

  const handleClose = () => {
    navigation.goBack();
  };

  const handleDownloadProfitabilityReport = async () => {
    if (isInvalidDownload) {
      return;
    }

    setIsLoading(true);

    await downloadProfitabilityReport({
      customerId: params.customerId,
      accountNumber: params.accountNumber,
      profitabilityType,
      initialMonth,
      initialYear,
    });

    setIsLoading(false);
  };

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={handleClose}
      direction={direction}
      width={sliderModalWidth}>
      <VStack
        w="full"
        py="inline.xxxs"
        borderTopRadius={{ base: Spacing['inline.xxxs'], lg: 0 }}
        backgroundColor={Color['neutral.white']}
        px="inline.xxs"
        space="inline.xxs">
        {directionIsBottom && (
          <Box
            w="55px"
            alignSelf="center"
            background={'neutral.lightest'}
            borderRadius={BoxRadius.sm}
            h={1}
          />
        )}
        <HStack
          borderTopRadius={{ base: BoxRadius.md, lg: 0 }}
          bg="neutral.white"
          space="stack.quarck"
          alignItems="center"
          justifyContent={'space-between'}>
          <Text
            variant="semibold"
            fontSize={Typography['fontsize.lg']}
            lineHeight="distant"
            color="neutral.black">
            Exportar relatório
          </Text>
          <Pressable testID={'button-close-filter-screen'} onPress={handleClose}>
            <Icon
              testID={'icon-close-filter-screen'}
              name="Close"
              size="xs"
              variant="rounded"
              {...(direction !== 'bottom' && { borderWidth: 0.05 })}
              backgroundColor="transparent"
              color={Color['brand.primary.principal']}
              borderColor={Color['brand.primary.principal']}
            />
          </Pressable>
        </HStack>
        <VStack space="inline.xxs">
          <Notice
            type="alert"
            textObjects={[
              { text: <Text>Versão Beta: </Text>, variant: 'semibold' },
              {
                text: <Text>Você está gerando o novo relatório, disponível em versão Beta.</Text>,
                variant: 'regular',
              },
            ]}
          />

          <VStack space="inline.xxxs" zIndex={2}>
            <Text variant="semibold">Tipo do Relatório</Text>
            <Select
              triggerProps={{ bg: Color.background }}
              placeholder="Selecione o tipo"
              options={profitabilityTypeOptions}
              selectedValue={profitabilityType}
              onValueChange={(value) =>
                setProfitabilityType(value as ReportingEnumsProfitabilityReportMarkingTypeEnum)
              }
            />
          </VStack>

          <VStack space="inline.xxxs" zIndex={2}>
            <Text variant="semibold">Selecione o Período</Text>
            <HStack w="full" space="stack.nano">
              <Box w="50%">
                <Select
                  triggerProps={{ bg: Color.background }}
                  placeholder="Mês"
                  options={monthsOptions}
                  selectedValue={initialMonth}
                  onValueChange={setInitialMonth}
                  maxOptionPerRender={8}
                  maxOptionsContentHeight="80vh"
                />
              </Box>
              <Box w="50%">
                <Select
                  triggerProps={{ bg: Color.background }}
                  placeholder="Ano"
                  options={yearsOptions}
                  selectedValue={initialYear}
                  onValueChange={setInitialYear}
                  maxOptionPerRender={8}
                  maxOptionsContentHeight="80vh"
                />
              </Box>
            </HStack>
          </VStack>

          <VStack space="inline.xxxs">
            <Button
              w="full"
              variant="primary"
              isLoading={isLoading}
              spinnerPlacement="end"
              isLoadingText="GERAR RELATÓRIO"
              isDisabled={isInvalidDownload}
              onPress={handleDownloadProfitabilityReport}>
              GERAR RELATÓRIO
            </Button>
          </VStack>
        </VStack>
      </VStack>
    </SliderModal>
  );
};
